import { Fragment, useCallback, useEffect, useState } from "react";
import { apiGet, apiSendVerbatim } from "../api/client";
import { HoverTip } from "./HoverTip";
import {
  CHANNEL_NAMES_INTRO, REMOVE_ALL_TIP, REMOVE_NAME_TIP, foundDate, groupKnownNames, groupTitle, nameSourceLabel,
  NOT_NEEDED_TIP, removeAllConfirm, verifiedLabel,
  type KnownChannelNames,
} from "../lib/channels";

// Settings > MeshCore: every channel name this server can read with, where it
// came from, and when. The owner can remove a name the scanner found; it then
// stays removed until allowed again. Read-only otherwise: nothing here transmits.

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function ChannelNamesManager() {
  const [data, setData] = useState<KnownChannelNames | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    apiGet<KnownChannelNames>("/api/channels/meshcore/names")
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e) => setError(errText(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function act(method: "DELETE" | "POST", path: string, name: string, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(name);
    try {
      await apiSendVerbatim(method, `${path}?name=${encodeURIComponent(name)}`);
      load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  }

  async function removeSource(source: string, n: number) {
    if (!window.confirm(removeAllConfirm(source, n))) return;
    setBusy(`source:${source}`);
    try {
      await apiSendVerbatim("DELETE", `/api/channels/meshcore/names/by-source?source=${encodeURIComponent(source)}`);
      load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  }

  const groups = data ? groupKnownNames(data.names) : [];

  return (
    <div className="rf-manager">
      <div className="settings-field-desc channel-names-intro">{CHANNEL_NAMES_INTRO}</div>
      {error && <div className="rf-error" role="alert">{error}</div>}
      {data === null && !error && <div className="empty-state">Loading…</div>}
      {data && (
        <div className="node-list-table-wrap">
          <table className="node-list-table rf-manager-table">
            <thead>
              <tr>
                <th>Channel name</th>
                <th>Where it came from</th>
                <th>Found</th>
                <th>Verified</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.key || "base"}>
                  <tr className="channel-names-group">
                    <td colSpan={4}>{groupTitle(g.key)} ({g.names.length})</td>
                    <td className="rf-row-actions">
                      {g.key !== "" && (
                        <HoverTip text={REMOVE_ALL_TIP}>
                          <button
                            type="button"
                            className="rf-link-btn rf-remove-btn"
                            disabled={busy === `source:${g.key}`}
                            onClick={() => void removeSource(g.key, g.names.length)}
                          >
                            Remove all {g.names.length}
                          </button>
                        </HoverTip>
                      )}
                    </td>
                  </tr>
                  {g.names.map((n) => (
                    <tr key={n.name}>
                      <td>{n.name}</td>
                      <td>{nameSourceLabel(n.source, n.found_in)}</td>
                      <td>{n.source === "config" ? "" : foundDate(n.found_at)}</td>
                      <td>{n.removable ? verifiedLabel(n.verified_on) : <HoverTip text={NOT_NEEDED_TIP}><span>not needed</span></HoverTip>}</td>
                      <td className="rf-row-actions">
                        {n.removable && (
                          <HoverTip text={REMOVE_NAME_TIP}>
                            <button
                              type="button"
                              className="rf-link-btn rf-remove-btn"
                              disabled={busy === n.name}
                              onClick={() => act("DELETE", "/api/channels/meshcore/names", n.name, `Remove ${n.name}? Its messages go back to unreadable.`)}
                            >
                              Remove
                            </button>
                          </HoverTip>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {data.names.length === 0 && (
                <tr><td colSpan={5}>No channel names yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data && data.removed.length > 0 && (
        <>
          <h3>Removed names</h3>
          <div className="settings-field-desc">The server will not add these again unless you allow them.</div>
          <ul className="channel-names-removed">
            {data.removed.map((name) => (
              <li key={name}>
                {name}{" "}
                <button type="button" className="rf-link-btn" disabled={busy === name} onClick={() => act("POST", "/api/channels/meshcore/names/allow", name)}>
                  Allow again
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
